/**
 * src/components/shared/RejectedLeadsButton.jsx -- TICKET-207
 *
 * Top-bar icon button (Inbox) that opens the RejectedLeadsModal.
 * Shows a count badge of pending rejections fetched on mount.
 * Matches LeadSyncButton's styling exactly.
 */
import { useState, useEffect, useCallback } from 'react';
import { Inbox } from 'lucide-react';
import RejectedLeadsModal from './RejectedLeadsModal';
import { useT } from '../../lib/i18n/I18nContext';
import { listRejectedLeads } from '../../lib/api/leads.js';

export default function RejectedLeadsButton() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);

  const refreshCount = useCallback(async () => {
    try {
      const res = await listRejectedLeads();
      if (res?.ok) setCount((res.rejections ?? []).length);
    } catch { /* non-critical -- badge just stays at 0 */ }
  }, []);

  useEffect(() => { refreshCount(); }, [refreshCount]);

  const handleClose = useCallback(() => {
    setOpen(false);
    // Refresh count after modal closes (user may have acted on items).
    refreshCount();
  }, [refreshCount]);

  return (
    <>
      <button
        data-testid="nav-rejected-leads-open"
        onClick={() => setOpen(true)}
        aria-label={t('rejectedLeads.title')}
        title={t('rejectedLeads.title')}
        style={{
          width: 44, height: 44, minHeight: 44, borderRadius: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: count > 0 ? 'var(--warn-bg, var(--accent-bg))' : 'var(--accent-bg)',
          color: count > 0 ? 'var(--warn, var(--accent))' : 'var(--accent)',
          border: 'none', boxShadow: 'inset 0 0 0 1px var(--bdr)',
          cursor: 'pointer', flexShrink: 0, transition: 'background 150ms var(--ease)',
          position: 'relative',
        }}
      >
        <Inbox size={20} strokeWidth={1.75} />
        {count > 0 && (
          <span style={{
            position: 'absolute', top: 6, right: 6,
            width: 8, height: 8, borderRadius: '50%',
            background: 'var(--bad)', display: 'block',
          }} />
        )}
      </button>
      <RejectedLeadsModal open={open} onClose={handleClose} />
    </>
  );
}
